import React, { useRef } from 'react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { Button, Text } from '@chakra-ui/react'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { DepositAccountingStatementInputs } from '@homevest/types/cashouts'
import { generateDepositAccountingStatementHTML } from '@homevest/utils/cashouts'

const DepositAccountingStatement: React.FC<DepositAccountingStatementInputs> = (
  depositAccountingStatementInputs
) => {
  const contentRef = useRef<HTMLDivElement>(null)

  const downloadPDF = async () => {
    if (!contentRef.current) return

    try {
      // Create PDF
      const pdf = new jsPDF('p', 'pt', 'a4')
      const pdfWidth = pdf.internal.pageSize.getWidth()

      // Set page dimensions for html2canvas
      const pageWidth = 816 // A4 width in pixels at 96 DPI
      const pageHeight = 1056 // A4 height in pixels at 96 DPI

      // Get the full content height
      const fullCanvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: pageWidth,
        height: contentRef.current.scrollHeight
      })

      const fullHeight = fullCanvas.height
      const fullWidth = fullCanvas.width

      // Calculate how many pages we need
      const pagesNeeded = Math.ceil(fullHeight / (pageHeight * 2)) // *2 because of scale

      for (let page = 0; page < pagesNeeded; page++) {
        if (page > 0) {
          pdf.addPage()
        }

        // Calculate the portion of the image to show on this page
        const sourceY = page * pageHeight * 2 // *2 because of scale
        const sourceHeight = Math.min(pageHeight * 2, fullHeight - sourceY)

        // Create a temporary canvas for this page
        const tempCanvas = document.createElement('canvas')
        const tempCtx = tempCanvas.getContext('2d')
        tempCanvas.width = fullWidth
        tempCanvas.height = sourceHeight

        // Draw the portion of the full image onto the temp canvas
        tempCtx?.drawImage(
          fullCanvas,
          0,
          sourceY,
          fullWidth,
          sourceHeight,
          0,
          0,
          fullWidth,
          sourceHeight
        )

        // Convert to data URL and add to PDF
        const imgData = tempCanvas.toDataURL('image/png')
        const imgWidth = pdfWidth
        const imgHeight = (sourceHeight * imgWidth) / fullWidth

        // Add margin to all pages except the first (which already has margin from the HTML)
        const marginTop = page === 0 ? 0 : 80 // 80pt margin for subsequent pages
        pdf.addImage(imgData, 'PNG', 0, marginTop, imgWidth, imgHeight)
      }

      // Download the PDF
      const tenantNames = depositAccountingStatementInputs.tenantNames
        .replace(/\s+/g, '-')
        .toLowerCase()
      pdf.save(`deposit-accounting-statement-${tenantNames}.pdf`)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Error generating PDF. Please try again.')
    }
  }

  return (
    <div>
      {/* hidden div containing raw HTML for deposit accounting statement */}
      <div
        className='absolute left-[-9999px]'
        ref={contentRef}
        dangerouslySetInnerHTML={{
          __html: generateDepositAccountingStatementHTML(depositAccountingStatementInputs)
        }}
      />
      <Button onClick={downloadPDF} colorScheme='teal' size='sm'>
        <ArrowDownTrayIcon height={20} />
        <Text ml={1}>Download PDF</Text>
      </Button>
    </div>
  )
}

export default DepositAccountingStatement
